import { useState, useMemo } from "react";
import { useLocation, Link } from "wouter";
import { useQuery } from "@tanstack/react-query";
import { cn } from "@/lib/utils";
import { getEnvBadgeConfig } from "@/lib/env-badge";
import { useBranding, DEFAULT_TAGLINE, LEGACY_DEFAULT_TAGLINE } from "@/hooks/use-branding";
import { usePermissions } from "@/hooks/use-permissions";
import { useAuth } from "@/hooks/use-auth";
import {
  Building2,
  BarChart,
  Users,
  FileText,
  Settings,
  AppWindow,
  CheckSquare,
  UserCheck,
  Mail,
  X,
  DollarSign,
  ChevronLeft,
  ChevronRight,
  Brain,
  MessageSquare,
  Clock,
  MapPin,
  LineChart,
  ClipboardList,
  Shield,
  Sun,
  Moon,
  Monitor,
  FlaskConical,
  MessageSquarePlus,
  Bot,
  ClipboardCheck,
} from "lucide-react";
import type { TrainingLabStatus } from "@shared/agentTrainingLab";
import { FEEDBACK_ADMIN_TAB_HREF, type FeedbackBoardStatus } from "@shared/feedbackBoard";
import { DOJO_PATH } from "@/components/agent-training-lab/routes";
import { AGENTS_REVIEW_PATH, AGENTS_TEST_DRIVE_PATH, useAgentQaStatus } from "@/components/agents-qa/use-agent-qa";
import { FEEDBACK_STATUS_KEY } from "@/components/feedback/use-feedback-board";
import { SiQuickbooks } from "react-icons/si";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useTheme } from "@/components/theme-provider";
import claimhiveIcon from "@/assets/claimhive-icon.svg";

// The current default tagline (DEFAULT_TAGLINE) already fits the sidebar header. Older firm rows
// may still store the legacy long default, which spills past the sidebar into the breadcrumb, so
// the sidebar swaps that legacy value for the current subtitle. A firm's own custom tagline is
// shown as-is.
const SIDEBAR_TAGLINE = DEFAULT_TAGLINE;

interface NavItem {
  name: string;
  href: string;
  icon: React.ComponentType<any>;
  /** Return true if the user has permission to see this item. Omit to always show. */
  isVisible?: (perms: ReturnType<typeof usePermissions>) => boolean;
}

const navigation: NavItem[] = [
  { name: "Dashboard", href: "/", icon: BarChart, isVisible: (p) => p.canViewDashboardOverview || p.canViewDashboardAdjuster },
  { name: "Claims", href: "/claims", icon: FileText, isVisible: (p) => p.canViewClaims },
  { name: "Leads", href: "/leads", icon: UserCheck, isVisible: (p) => p.canViewLeads },
  { name: "Tasks", href: "/tasks", icon: CheckSquare, isVisible: (p) => p.canViewTasks },
];

const secondaryNavigation: NavItem[] = [
  { name: "Email", href: "/email", icon: Mail, isVisible: (p) => !p.isViewer && (p.canSendCommunications || p.canViewCommunications) },
  { name: "Team Chat", href: "/chat", icon: MessageSquare }, // always visible
  { name: "Contacts", href: "/contacts", icon: Users, isVisible: (p) => p.canViewContacts },
  { name: "Carriers", href: "/carriers", icon: Building2, isVisible: (p) => p.canViewContacts },
  { name: "Claims Map", href: "/claims-map", icon: MapPin, isVisible: (p) => p.canViewClaims },
  { name: "Apps", href: "/apps", icon: AppWindow, isVisible: (p) => p.canUseDraftingAI },
];


const superadminNavigation: NavItem[] = [
  { name: "Organizations", href: "/superadmin/organizations", icon: Shield },
  { name: "Beta", href: "/superadmin/beta", icon: FlaskConical },
];

const tertiaryNavigation: NavItem[] = [
  { name: "CFO", href: "/cfo", icon: LineChart, isVisible: (p) => p.isAdmin },
  { name: "Activity Tracking", href: "/activity-tracking", icon: Clock, isVisible: (p) => p.canViewTasks },
  { name: "Accounting", href: "/accounting", icon: DollarSign, isVisible: (p) => p.isAdmin },
  { name: "Commissions", href: "/commissions", icon: DollarSign, isVisible: (p) => p.canViewAllCommissions },
  { name: "Reports", href: "/reports", icon: BarChart, isVisible: (p) => p.canViewOperationalReports },
  { name: "Timesheets", href: "/timesheets", icon: ClipboardList, isVisible: (p) => p.canViewFinancialReports },
  { name: "QuickBooks", href: "/quickbooks", icon: SiQuickbooks, isVisible: (p) => p.isAdmin || p.canAccessQuickBooks },
  { name: "AI Corrections", href: "/ai-corrections", icon: Brain, isVisible: (p) => p.isAdmin },
  // Also hidden unless the Dojo's status endpoint answers (feature flag on, not production).
  { name: "Dojo", href: DOJO_PATH, icon: FlaskConical, isVisible: (p) => p.isAdmin },
  // These two are also hidden unless the Run agent status route answers (ENABLE_AGENT_QA_RUN on, not production).
  { name: "Agents test drive", href: AGENTS_TEST_DRIVE_PATH, icon: Bot, isVisible: (p) => p.isAdmin },
  { name: "Agents review", href: AGENTS_REVIEW_PATH, icon: ClipboardCheck, isVisible: (p) => p.isAdmin },
  // Also hidden unless the Bugs & Features board's status endpoint answers (FEEDBACK_BOARD_ENABLED on).
  { name: "Feedback", href: FEEDBACK_ADMIN_TAB_HREF, icon: MessageSquarePlus, isVisible: (p) => p.isAdmin },
  { name: "Settings", href: "/settings", icon: Settings },
];

/** "/feedback?tab=admin" → "/feedback", so a nav item with a query string still highlights on its page. */
const hrefPath = (href: string) => href.split("?")[0];

interface SidebarProps {
  isMobileOpen?: boolean;
  setIsMobileOpen?: (open: boolean) => void;
  isCollapsed?: boolean;
  setIsCollapsed?: (collapsed: boolean) => void;
}

export default function Sidebar({ isMobileOpen = false, setIsMobileOpen, isCollapsed = false, setIsCollapsed }: SidebarProps = {}) {
  const [location] = useLocation();
  const settings = useBranding();
  const { displayName, tagline, logoSmallUrl, logoUrl } = settings;
  // Swap only the legacy long default tagline for the shorter sidebar subtitle; keep any custom firm tagline.
  const sidebarTagline = tagline === LEGACY_DEFAULT_TAGLINE ? SIDEBAR_TAGLINE : tagline;
  const perms = usePermissions();
  const { user } = useAuth();
  const { data: userOrgs = [] } = useQuery<{ id: string; name: string }[]>({
    queryKey: ["/api/auth/user-organizations"],
    enabled: !!user,
  });
  const currentOrgName = userOrgs.find(o => o.id === user?.activeOrgId)?.name;
  const isMultiOrg = userOrgs.length > 1;

  // Filter nav items by permission. Show all while loading to avoid layout flash.
  const hasAccounting = !!(user?.membership?.canAccessAccounting || user?.membership?.isOrgAdmin || user?.isSuperAdmin);
  const hasClaimsSeat = user?.membership ? user.membership.hasClaimsAccess : true;
  const filterNav = (items: NavItem[]) =>
    perms.isLoading ? items : items.filter((item) => {
      if (item.href === '/accounting') return hasAccounting;
      if ((item.href === '/claims' || item.href === '/claims-map') && !hasClaimsSeat) return false;
      return !item.isVisible || item.isVisible(perms);
    });

  const visibleNavigation = useMemo(() => filterNav(navigation), [perms]);
  const visibleSecondaryNavigation = useMemo(() => filterNav(secondaryNavigation), [perms]);
  // The status route is only mounted when the flag is on outside production; 404/403 → no data → hidden.
  const { data: trainingLabStatus } = useQuery<TrainingLabStatus>({
    queryKey: ["/api/agent-training-lab/status"],
    enabled: !perms.isLoading && perms.isAdmin && import.meta.env.VITE_APP_ENV !== "production",
    retry: false,
  });
  const showDojo = !perms.isLoading && !!trainingLabStatus;
  // Same key as the board's own status query, so they share the cache. 404 while FEEDBACK_BOARD_ENABLED is off → hidden.
  const { data: feedbackBoardStatus } = useQuery<FeedbackBoardStatus>({
    queryKey: FEEDBACK_STATUS_KEY,
    enabled: !perms.isLoading && !!perms.isAdmin,
    retry: false,
  });
  const showFeedback = !perms.isLoading && feedbackBoardStatus?.enabled === true;
  const { enabled: agentQaEnabled } = useAgentQaStatus();
  const showAgentsTestDrive = !perms.isLoading && agentQaEnabled;
  const visibleTertiaryNavigation = useMemo(
    () =>
      filterNav(tertiaryNavigation).filter(
        (item) =>
          (item.href !== DOJO_PATH || showDojo) &&
          (item.href !== FEEDBACK_ADMIN_TAB_HREF || showFeedback) &&
          ((item.href !== AGENTS_TEST_DRIVE_PATH && item.href !== AGENTS_REVIEW_PATH) || showAgentsTestDrive)
      ),
    [perms, showDojo, showFeedback, showAgentsTestDrive]
  );
  const visibleSuperadminNavigation = useMemo(
    () => (perms.isSuperAdmin ? superadminNavigation : []),
    [perms.isSuperAdmin]
  );

  // Fetch unread chat count for badge
  // Note: Invalidated automatically via WebSocket or polling fallback
  // from useRealtimeUpdates hook (mounted in enhanced-inbox)
  const { data: unreadData } = useQuery<{ count: number }>({
    queryKey: ["/api/chat/unread-count"],
  });
  const unreadChatCount = unreadData?.count || 0;

  const closeMobileSidebar = () => {
    if (setIsMobileOpen) {
      setIsMobileOpen(false);
    }
  };

  return (
    <>
      {/* Mobile backdrop */}
      {isMobileOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 lg:hidden cursor-pointer"
          onClick={closeMobileSidebar}
          onTouchEnd={closeMobileSidebar}
          data-testid="sidebar-backdrop"
          role="button"
          aria-label="Close sidebar"
        />
      )}

      {/* Sidebar */}
      <div
        className={cn(
          "fixed inset-y-0 left-0 z-50 bg-card border-r border-border transition-transform duration-300 ease-in-out lg:translate-x-0 flex flex-col",
          isCollapsed ? "w-64 lg:w-20" : "w-64",
          isMobileOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
        )}
        data-testid="sidebar-container"
      >
        {/* Header */}
        <div className="flex h-16 items-center border-b border-border px-6 flex-shrink-0">
          <div className={cn("flex items-center flex-1 min-w-0 space-x-2", isCollapsed && "lg:justify-center lg:space-x-0")}>
            {(logoSmallUrl || settings?.logoUrl) ? (
              <Link href="/" className="flex items-center">
                <img 
                  src={logoSmallUrl || settings?.logoUrl || ''} 
                  alt="Logo" 
                  className="h-8 w-8 object-contain rounded cursor-pointer" 
                  onError={(e) => {
                    // Fallback to the ClaimHive icon if the branded image fails to load
                    e.currentTarget.style.display = 'none';
                    const fallback = e.currentTarget.nextElementSibling;
                    if (fallback) (fallback as HTMLElement).style.display = 'flex';
                  }}
                />
              </Link>
            ) : null}
            <Link href="/" className="h-8 w-8 rounded flex items-center justify-center flex-shrink-0 cursor-pointer" style={{ display: (logoSmallUrl || settings?.logoUrl) ? 'none' : 'flex' }}>
              <img src={claimhiveIcon} alt="ClaimHive" className="h-8 w-8 object-contain rounded" />
            </Link>
            <Link href="/" className={cn("min-w-0 overflow-hidden cursor-pointer", isCollapsed && "lg:hidden")}>
              <h1 className="text-sm font-semibold text-foreground truncate" title={displayName}>{displayName}</h1>
              <p className="text-xs text-muted-foreground truncate" title={sidebarTagline}>{sidebarTagline}</p>
            </Link>
            {isMultiOrg && (
              <Link href="/select-org" className={cn("block mt-1 px-1", isCollapsed && "lg:hidden")}>
                <span className="text-xs text-muted-foreground hover:text-foreground transition-colors truncate">
                  {currentOrgName ?? "Switch org"} ↓
                </span>
              </Link>
            )}
          </div>
          
          {/* Mobile close button */}
          <Button
            variant="ghost"
            size="sm"
            className="lg:hidden shrink-0"
            onClick={closeMobileSidebar}
            data-testid="sidebar-close"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
        
        {/* Navigation Container - Scrollable */}
        <div className="flex-1 overflow-y-auto">
          
          {/* Scrollable Navigation */}
          <div className="overscroll-contain">
            <TooltipProvider>
              <nav className={cn("mt-6 pb-6", isCollapsed ? "px-3 lg:px-2" : "px-3")}>
                <div className="space-y-1">
                  {visibleNavigation.map((item) => {
                    const isActive = location === item.href;
                    const Icon = item.icon;
                    const showBadge = item.name === "Team Chat" && unreadChatCount > 0;
                    
                    const navLink = (
                      <Link
                        key={item.name}
                        href={item.href}
                        onClick={closeMobileSidebar}
                        className={cn(
                          "flex items-center text-sm font-medium rounded-md transition-colors relative",
                          isCollapsed ? "px-3 py-2 lg:justify-center lg:px-2" : "px-3 py-2",
                          isActive
                            ? "bg-accent text-accent-foreground"
                            : "text-muted-foreground hover:bg-accent hover:text-accent-foreground"
                        )}
                        data-testid={`nav-${item.name.toLowerCase().replace(' ', '-')}`}
                      >
                        <div className="relative">
                          <Icon className={cn("h-4 w-4 mr-3", isCollapsed && "lg:mr-0")} />
                          {showBadge && isCollapsed && (
                            <span className="absolute -top-1 -right-1 h-4 w-4 rounded-full bg-red-500 text-white text-xs flex items-center justify-center" data-testid="badge-chat-unread">
                              {unreadChatCount > 9 ? '9+' : unreadChatCount}
                            </span>
                          )}
                        </div>
                        <span className={cn("flex-1", isCollapsed && "lg:hidden")}>{item.name}</span>
                        {showBadge && (
                          <span className={cn("ml-2 h-5 min-w-5 px-1 rounded-full bg-red-500 text-white text-xs flex items-center justify-center", isCollapsed && "lg:hidden")} data-testid="badge-chat-unread">
                            {unreadChatCount > 99 ? '99+' : unreadChatCount}
                          </span>
                        )}
                      </Link>
                    );
                    
                    if (isCollapsed) {
                      return (
                        <Tooltip key={item.name} delayDuration={0}>
                          <TooltipTrigger asChild>
                            {navLink}
                          </TooltipTrigger>
                          <TooltipContent side="right">
                            {item.name}
                            {showBadge && ` (${unreadChatCount} unread)`}
                          </TooltipContent>
                        </Tooltip>
                      );
                    }
                    
                    return navLink;
                  })}
                </div>
                
                {visibleSecondaryNavigation.length > 0 && (
                <div className="mt-8 pt-6 border-t border-border">
                  <div className="space-y-1">
                    {visibleSecondaryNavigation.map((item) => {
                      const Icon = item.icon;
                      const isActive = location === item.href;

                      const navLink = (
                        <Link
                          key={item.name}
                          href={item.href}
                          onClick={closeMobileSidebar}
                          className={cn(
                            "flex items-center text-sm font-medium rounded-md transition-colors",
                            isCollapsed ? "px-3 py-2 lg:justify-center lg:px-2" : "px-3 py-2",
                            isActive
                              ? "bg-accent text-accent-foreground"
                              : "text-muted-foreground hover:bg-accent hover:text-accent-foreground"
                          )}
                          data-testid={`nav-${item.name.toLowerCase().replace(' ', '-')}`}
                        >
                          <Icon className={cn("h-4 w-4 mr-3", isCollapsed && "lg:mr-0")} />
                          <span className={cn(isCollapsed && "lg:hidden")}>{item.name}</span>
                        </Link>
                      );
                      
                      if (isCollapsed) {
                        return (
                          <Tooltip key={item.name} delayDuration={0}>
                            <TooltipTrigger asChild>
                              {navLink}
                            </TooltipTrigger>
                            <TooltipContent side="right">
                              {item.name}
                            </TooltipContent>
                          </Tooltip>
                        );
                      }
                      
                      return navLink;
                    })}
                  </div>
                </div>
                )}
                
                {visibleTertiaryNavigation.length > 0 && (
                <div className="mt-8 pt-6 border-t border-border">
                  <div className="space-y-1">
                    {visibleTertiaryNavigation.map((item) => {
                      const Icon = item.icon;
                      const isActive = location === hrefPath(item.href);

                      const navLink = (
                        <Link
                          key={item.name}
                          href={item.href}
                          onClick={closeMobileSidebar}
                          className={cn(
                            "flex items-center text-sm font-medium rounded-md transition-colors",
                            isCollapsed ? "px-3 py-2 lg:justify-center lg:px-2" : "px-3 py-2",
                            isActive
                              ? "bg-accent text-accent-foreground"
                              : "text-muted-foreground hover:bg-accent hover:text-accent-foreground"
                          )}
                          data-testid={`nav-${item.name.toLowerCase().replace(' ', '-')}`}
                        >
                          <Icon className={cn("h-4 w-4 mr-3", isCollapsed && "lg:mr-0")} />
                          <span className={cn(isCollapsed && "lg:hidden")}>{item.name}</span>
                        </Link>
                      );

                      if (isCollapsed) {
                        return (
                          <Tooltip key={item.name} delayDuration={0}>
                            <TooltipTrigger asChild>
                              {navLink}
                            </TooltipTrigger>
                            <TooltipContent side="right">
                              {item.name}
                            </TooltipContent>
                          </Tooltip>
                        );
                      }

                      return navLink;
                    })}
                  </div>
                </div>
                )}

                {visibleSuperadminNavigation.length > 0 && (
                  <div className="mt-8 pt-6 border-t border-border">
                    <p className={cn("px-3 mb-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider", isCollapsed && "lg:hidden")}>
                      Platform
                    </p>
                    <div className="space-y-1">
                      {visibleSuperadminNavigation.map((item) => {
                        const Icon = item.icon;
                        const isActive = location === item.href;
                        const navLink = (
                          <Link
                            key={item.name}
                            href={item.href}
                            onClick={closeMobileSidebar}
                            className={cn(
                              "flex items-center text-sm font-medium rounded-md transition-colors",
                              isCollapsed ? "px-3 py-2 lg:justify-center lg:px-2" : "px-3 py-2",
                              isActive
                                ? "bg-accent text-accent-foreground"
                                : "text-muted-foreground hover:bg-accent hover:text-accent-foreground"
                            )}
                            data-testid={`nav-${item.name.toLowerCase().replace(' ', '-')}`}
                          >
                            <Icon className={cn("h-4 w-4 mr-3", isCollapsed && "lg:mr-0")} />
                            <span className={cn(isCollapsed && "lg:hidden")}>{item.name}</span>
                          </Link>
                        );
                        if (isCollapsed) {
                          return (
                            <Tooltip key={item.name} delayDuration={0}>
                              <TooltipTrigger asChild>{navLink}</TooltipTrigger>
                              <TooltipContent side="right">{item.name}</TooltipContent>
                            </Tooltip>
                          );
                        }
                        return navLink;
                      })}
                    </div>
                  </div>
                )}
              </nav>
            </TooltipProvider>
          </div>
        </div>

        {/* Theme Toggle */}
        {(() => {
          const { theme, setTheme } = useTheme();
          const nextTheme = theme === "light" ? "dark" : theme === "dark" ? "system" : "light";
          const ThemeIcon = theme === "light" ? Sun : theme === "dark" ? Moon : Monitor;
          const label = theme === "light" ? "Light" : theme === "dark" ? "Dark" : "System";

          const btn = (
            <Button
              variant="ghost"
              size="sm"
              className={cn("flex items-center w-full justify-start px-3 text-muted-foreground hover:text-foreground", isCollapsed && "lg:justify-center")}
              onClick={() => setTheme(nextTheme)}
              data-testid="theme-toggle"
            >
              <ThemeIcon className={cn("h-4 w-4 mr-3", isCollapsed && "lg:mr-0")} />
              <span className={cn("text-sm", isCollapsed && "lg:hidden")}>{label}</span>
            </Button>
          );

          return (
            <div className="flex-shrink-0 border-t border-border px-2 py-2">
              {isCollapsed ? (
                <Tooltip delayDuration={0}>
                  <TooltipTrigger asChild>{btn}</TooltipTrigger>
                  <TooltipContent side="right">Theme: {label}</TooltipContent>
                </Tooltip>
              ) : btn}
            </div>
          );
        })()}

        {/* Environment & Version Footer */}
        {(() => {
          const env = (import.meta.env.VITE_APP_ENV || 'local').toLowerCase();
          const version = typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : '0.0.0';
          const config = getEnvBadgeConfig(env);

          if (isCollapsed) {
            return (
              <>
                <div className="flex-shrink-0 border-t border-border px-4 py-2.5 lg:hidden">
                  <div className={cn("flex items-center justify-between rounded-md border px-2.5 py-1.5 text-xs", config.bg)}>
                    <div className="flex items-center gap-1.5">
                      <span className={cn("h-2 w-2 rounded-full", config.dot)} />
                      <span className={cn("font-semibold tracking-wide", config.sidebarColor)}>{config.label}</span>
                    </div>
                    <span className="text-muted-foreground font-mono">v{version}</span>
                  </div>
                </div>
                <div className="hidden lg:flex flex-shrink-0 border-t border-border px-2 py-3 justify-center">
                  <Tooltip delayDuration={0}>
                    <TooltipTrigger asChild>
                      <div className={cn("h-3 w-3 rounded-full", config.dot)} />
                    </TooltipTrigger>
                    <TooltipContent side="right">
                      {config.label} · v{version}
                    </TooltipContent>
                  </Tooltip>
                </div>
              </>
            );
          }

          return (
            <div className="flex-shrink-0 border-t border-border px-4 py-2.5">
              <div className={cn("flex items-center justify-between rounded-md border px-2.5 py-1.5 text-xs", config.bg)}>
                <div className="flex items-center gap-1.5">
                  <span className={cn("h-2 w-2 rounded-full", config.dot)} />
                  <span className={cn("font-semibold tracking-wide", config.sidebarColor)}>{config.label}</span>
                </div>
                <span className="text-muted-foreground font-mono">v{version}</span>
              </div>
            </div>
          );
        })()}
      </div>

      {/* Desktop collapse toggle button - fixed position to avoid overflow clipping */}
      <Button
        variant="outline"
        size="sm"
        className={cn(
          "hidden lg:flex fixed top-20 h-7 w-7 rounded-full border-2 border-border bg-background p-0 items-center justify-center hover:bg-accent hover:border-primary shadow-md transition-all z-50",
          isCollapsed ? "left-[calc(5rem-1.075rem)]" : "left-[calc(16rem-1.075rem)]"
        )}
        onClick={() => setIsCollapsed && setIsCollapsed(!isCollapsed)}
        data-testid="sidebar-toggle"
        title={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
      >
        {isCollapsed ? (
          <ChevronRight className="h-4 w-4" />
        ) : (
          <ChevronLeft className="h-4 w-4" />
        )}
      </Button>
    </>
  );
}
